import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiGet, apiPatch, apiPost, apiPut } from "../api/client";

export type PlayState =
  | "draft"
  | "approved"
  | "running"
  | "paused"
  | "replanning"
  | "completed"
  | "abandoned";

export type StepType =
  | "decision"
  | "comm"
  | "query"
  | "micro_task"
  | "wait"
  | "milestone"
  | "instrumentation";

export type StepOwnerType = "operator" | "agent" | "approver" | "cxo";

export type StepState =
  | "pending"
  | "ready"
  | "running"
  | "awaiting_review"
  | "completed"
  | "failed"
  | "paused"
  | "skipped";

/**
 * One measurable success metric attached to a play. Mirrors the
 * server's `SuccessMetric` shape (see apps/api/src/db/repos/plays.ts).
 */
export interface SuccessMetric {
  label: string;
  target: string;
  baseline?: string;
  measurement_window?: string;
  horizon_weeks?: number;
}

export interface PlayRow {
  id: string;
  workspaceId: string;
  roadmapEntryId: string | null;
  state: PlayState;
  stepGraphVersion: number;
  ownerUserId: string;
  archetype: string;
  /** Captured outcome — Discovery Phase 1 or template/quick form. */
  outcomeStatement: string | null;
  /** Captured success metrics — Discovery Phase 6 or template/quick form. */
  successMetrics: SuccessMetric[];
  targetWindowStart: string | null;
  targetWindowEnd: string | null;
  replanCount: number;
  createdAt: string;
  approvedAt: string | null;
  startedAt: string | null;
  pausedAt: string | null;
  pausedReason: string | null;
  completedAt: string | null;
  abandonedAt: string | null;
  abandonedReason: string | null;
  /** GamePlan membership — null when the play isn't in a campaign. */
  /** Campaign title — denormalised so the badge doesn't need a separate fetch. */
}

export interface StepRow {
  id: string;
  playId: string;
  ord: number;
  type: StepType;
  title: string;
  description: string;
  ownerType: StepOwnerType;
  ownerUserId: string | null;
  agentBindingId: string | null;
  state: StepState;
  inputs: Record<string, unknown>;
  output: unknown | null;
  successCriteria: string;
  requiresSignoff: boolean;
  signoffUserId: string | null;
  claimedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  retryCount: number;
  // Resolved by the API: COALESCE(steps.display_owner, user.display_name,
  // agent_definition.name, owner_type). Always set on play-detail responses.
  displayOwner: string;
  // True when the step has an agent_binding_id — runs unattended, surfaced
  // with ⚡ in Timeline/Graph/Kanban + "Mode: Autonomous" in Inspector.
  isAutonomous: boolean;
  // Underlying agent_definition.name when bound (e.g., "copilot.m365");
  // null for human-owned steps.
  agentName: string | null;
  // Set when the step is assigned to a Hand agent from the library.
  handId: string | null;
}

export interface StepDependencyRow {
  upstreamStepId: string;
  downstreamStepId: string;
}

export interface StepEventRow {
  id: string;
  stepId: string;
  eventType: string;
  actorUserId: string | null;
  actorKind: "user" | "system" | "agent";
  payload: Record<string, unknown>;
  occurredAt: string;
}

export interface PlayDetail {
  play: PlayRow;
  steps: StepRow[];
  dependencies: StepDependencyRow[];
  recentEvents: StepEventRow[];
  /**
   * Where the play came from. null = XI-promoted (default).
   * "template_direct" = created via Use this template in the catalog.
   * Drives the source badge on the play header.
   */
  lineageSource?: string | null;
}

export function playQueryKey(workspaceId: string, playId: string) {
  return ["play", workspaceId, playId] as const;
}

export function usePlay(workspaceId: string, playId: string) {
  return useQuery<PlayDetail, Error>({
    queryKey: playQueryKey(workspaceId, playId),
    enabled: Boolean(workspaceId) && Boolean(playId),
    queryFn: () =>
      apiGet<PlayDetail>(
        `/api/workspaces/${workspaceId}/plays/${playId}`,
      ),
  });
}

export type ReplanTriggerKind =
  | "agent_failure"
  | "stakeholder_change"
  | "external_blocker"
  | "cxo_directed";

export interface RequestReplanArgs {
  trigger_kind: ReplanTriggerKind;
  rationale: string;
}

export function usePlayMutations(workspaceId: string, playId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: playQueryKey(workspaceId, playId) });

  const approve = useMutation({
    mutationFn: () =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/approve`,
        {},
      ),
    onSuccess: invalidate,
  });
  const start = useMutation({
    mutationFn: () =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/start`,
        {},
      ),
    onSuccess: invalidate,
  });
  const pause = useMutation({
    mutationFn: (rationale: string) =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/pause`,
        { rationale },
      ),
    onSuccess: invalidate,
  });
  const resume = useMutation({
    mutationFn: () =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/resume`,
        {},
      ),
    onSuccess: invalidate,
  });
  const abandon = useMutation({
    mutationFn: (rationale: string) =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/abandon`,
        { rationale },
      ),
    onSuccess: invalidate,
  });
  const requestReplan = useMutation({
    mutationFn: (args: RequestReplanArgs) =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/request-replan`,
        args,
      ),
    onSuccess: invalidate,
  });

  return { approve, start, pause, resume, abandon, requestReplan };
}

export interface ClaimStepResult {
  step: StepRow;
}

export function useRunAgent(workspaceId: string, playId: string) {
  const qc = useQueryClient();
  const timersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((id) => clearTimeout(id));
      timersRef.current = [];
    };
  }, []);

  return useMutation<{ job_id: string; status: string; dedup?: boolean }, Error, string>({
    mutationFn: (stepId: string) =>
      apiPost(
        `/api/workspaces/${workspaceId}/plays/${playId}/steps/${stepId}/run-agent`,
        {},
      ),
    onSuccess: () => {
      // Refresh the play immediately, and again after a short interval so
      // the user sees the step transition once the worker picks it up.
      void qc.invalidateQueries({ queryKey: playQueryKey(workspaceId, playId) });
      timersRef.current.push(
        setTimeout(() => {
          void qc.invalidateQueries({ queryKey: playQueryKey(workspaceId, playId) });
        }, 4000),
      );
      timersRef.current.push(
        setTimeout(() => {
          void qc.invalidateQueries({ queryKey: playQueryKey(workspaceId, playId) });
        }, 8000),
      );
    },
  });
}

export function useStepMutations(workspaceId: string, playId: string) {
  const qc = useQueryClient();
  const invalidate = () =>
    qc.invalidateQueries({ queryKey: playQueryKey(workspaceId, playId) });

  const claim = useMutation({
    mutationFn: (stepId: string) =>
      apiPost<ClaimStepResult>(`/api/steps/${stepId}/claim`, {}),
    onSuccess: invalidate,
  });
  const complete = useMutation({
    mutationFn: (args: {
      stepId: string;
      output: Record<string, unknown>;
      contextAnnotation?: {
        key: string;
        value: unknown;
        confidence: "confirmed" | "suspected" | "unconfirmed";
      };
    }) =>
      apiPost<ClaimStepResult>(`/api/steps/${args.stepId}/complete`, {
        output: args.output,
        ...(args.contextAnnotation && {
          contextAnnotation: args.contextAnnotation,
        }),
      }),
    onSuccess: invalidate,
  });
  const fail = useMutation({
    mutationFn: (args: { stepId: string; error: string; retry?: boolean }) =>
      apiPost(`/api/steps/${args.stepId}/fail`, {
        error: args.error,
        retry: args.retry ?? false,
      }),
    onSuccess: invalidate,
  });
  const reject = useMutation({
    mutationFn: (args: { stepId: string; reason: string }) =>
      apiPost(`/api/steps/${args.stepId}/reject`, { reason: args.reason }),
    onSuccess: invalidate,
  });

  return { claim, complete, fail, reject };
}

export interface PlayListItem {
  id: string;
  workspaceId: string;
  archetype: string;
  state: PlayState;
  ownerUserId: string;
  targetWindowEnd: string | null;
  replanCount: number;
  createdAt: string;
  [k: string]: unknown;
}

export interface PlayListResponse {
  items: PlayListItem[];
  nextCursor: string | null;
}

export function usePlaysList(
  workspaceId: string,
  state?: PlayState | PlayState[],
) {
  const stateParam = Array.isArray(state) ? state.join(",") : state;
  return useQuery<PlayListResponse, Error>({
    queryKey: ["plays", workspaceId, stateParam ?? "all"],
    enabled: Boolean(workspaceId),
    queryFn: () =>
      apiGet<PlayListResponse>(
        `/api/workspaces/${workspaceId}/plays${
          stateParam ? `?state=${encodeURIComponent(stateParam)}` : ""
        }`,
      ),
  });
}

export interface ChatTurn {
  id: string;
  role: "user" | "assistant";
  content: string;
}

export interface ProposedStep {
  id: string;
  title: string;
  description?: string;
  requiresApproval?: boolean;
}

export interface ReplaceStepsArgs {
  steps: ProposedStep[];
}

export function useReplaceSteps(workspaceId: string, playId: string) {
  const qc = useQueryClient();
  return useMutation<{ steps: StepRow[] }, Error, ReplaceStepsArgs>({
    mutationFn: ({ steps }) =>
      apiPut<{ steps: StepRow[] }>(
        `/api/workspaces/${workspaceId}/plays/${playId}/steps`,
        { steps },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: playQueryKey(workspaceId, playId) });
    },
  });
}

export type AssignStepArgs =
  | { stepId: string; handId: string; bindingId?: undefined }
  | { stepId: string; bindingId: string; handId?: undefined }
  | { stepId: string; handId: null; bindingId?: undefined };

export function useAssignStep(workspaceId: string, playId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ stepId, handId, bindingId }: AssignStepArgs) => {
      if (bindingId) return apiPatch(`/api/steps/${stepId}/assign`, { mode: "binding", binding_id: bindingId });
      if (handId) return apiPatch(`/api/steps/${stepId}/assign`, { mode: "agent", hand_id: handId });
      return apiPatch(`/api/steps/${stepId}/assign`, { mode: "operator" });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: playQueryKey(workspaceId, playId) });
    },
  });
}
